import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../utils/Api";
import foodImage from "../assets/gallery3.avif";

const WHATSAPP_NUMBER = "447368325136";

// Shared Tailwind classes
const infoCard = "bg-gradient-to-b from-[#161616] to-[#0d0d0d] border border-white/10 rounded-2xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.35)]";
const detailRow = "flex items-center gap-3 py-3 border-t border-white/[0.06] first:border-t-0 first:pt-0";
const detailIcon = "w-10 h-10 min-w-[40px] rounded-xl flex items-center justify-center bg-brand-orange/10 text-brand-orange text-[15px]";
const detailTextWrap = "flex flex-col gap-0.5 min-w-0";
const whatsappLink = "flex w-full items-center justify-center gap-2 mt-5 mb-3 px-4 py-2.5 rounded-full border border-[#248648]/40 bg-[#248648]/10 font-semibold text-[14px] no-underline text-[#3ddc84] transition-all hover:bg-[#248648]/20";

export default function OrderTracking() {
  const { apiFetch } = useApi();
  const [status, setStatus] = useState("to do");
  const [orderId, setOrderId] = useState("");
  const [orderType, setOrderType] = useState("");
  const [statusSource, setStatusSource] = useState("local");
  const [driver, setDriver] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const encryptedId = searchParams.get("id");

  const formatApiStatus = (value) => String(value || "").replace(/[_-]+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase());
  const formatEtaOnlyMinutes = (value) => {
    if (!value) return "";
    const str = String(value).trim(); const firstPart = str.split(",")[0]?.trim();
    return (/min/i.test(firstPart) ? firstPart : firstPart) || str;
  };

  useEffect(() => {
    if (!encryptedId) { setError("Missing order ID in URL"); setLoading(false); return; }
    let interval = null; let isMounted = true; let inFlight = false; let firstLoad = true;
    async function fetchStatus(forceRefresh = false) {
      if (inFlight) return; inFlight = true;
      try {
        const data = await apiFetch("order-status", { encrypted_order_id: encryptedId, force_refresh: forceRefresh ? 1 : 0 });
        if (!isMounted) return;
        if (!data.success || !data.order) { if (firstLoad) { setError(data.message || "Invalid order"); setLoading(false); } return; }
        const order = data.order;
        setStatus(String(order.status || "").trim());
        setOrderId(order.id || ""); setOrderType(String(order.order_type || "").toLowerCase().trim());
        setStatusSource(String(order.status_source || "local").toLowerCase().trim());
        setDriver(order.driver || null); setEta(order.eta || null);
        setLoading(false); setError(null); firstLoad = false;
      } catch (err) {
        if (!isMounted) return; console.error("Failed to fetch order:", err);
        if (firstLoad) { setError("Network or server error"); setLoading(false); }
      } finally { inFlight = false; firstLoad = false; }
    }
    fetchStatus(true); interval = setInterval(() => fetchStatus(false), 10000);
    return () => { isMounted = false; if (interval) clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encryptedId]);

  const isDelivery = orderType === "delivery";
  const lowerStatus = String(status || "").toLowerCase().trim();
  const showOriginalThreeStepDesign = !isDelivery || (isDelivery && (statusSource === "local" || statusSource === "handoff"));
  const steps = [
    { key: "to do", label: "Order Received", subtext: "Your order is in queue", icon: "fa-receipt" },
    { key: "in progress", label: "Being Prepared", subtext: "Your order is being prepared.", icon: "fa-kitchen-set" },
    { key: "completed", label: isDelivery ? "Order Ready for Delivery" : "Order Ready", subtext: isDelivery ? "Your order is ready for delivery." : "Your order is ready for collection.", icon: isDelivery ? "fa-truck-fast" : "fa-bag-shopping" },
  ];
  const originalTrackingConfig = useMemo(() => {
    const stepOrder = { "to do": 0, "in progress": 1, completed: 2 };
    const currentStep = stepOrder[lowerStatus] ?? 0;
    return { currentStep };
  }, [lowerStatus]);

  const showDriverDetails = isDelivery && statusSource === "naijameals" && !["done", "completed", "delivered"].includes(lowerStatus) && (driver || eta?.pickup || eta?.dropoff);

  const pageBase = "min-h-screen bg-black text-white px-5 pb-16 max-w-[460px] mt-[110px] mx-auto font-poppins";
  const orderTypeLabel = isDelivery ? "Delivery" : orderType === "pick up" ? "Pick Up" : orderType ? formatApiStatus(orderType) : "Order";

  if (loading) {
    return (
      <div className={pageBase}>
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-white/15 border-t-brand-orange" />
          <p className="text-[15px] text-white/60">Loading order status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageBase}>
        <div className="flex flex-col items-center gap-3 py-20 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ff5252]/10 text-[#ff5252] text-2xl">
            <i className="fas fa-triangle-exclamation" />
          </div>
          <p className="font-prata text-lg font-bold">Something went wrong</p>
          <p className="text-sm text-white/50 max-w-[280px]">{error}</p>
        </div>
      </div>
    );
  }

  if (lowerStatus === "cancelled") {
    return (
      <div className={pageBase}>
        <header className="flex items-center gap-3 flex-wrap mb-5 pb-4 border-b border-[rgba(255,82,82,0.3)]">
          <h1 className="font-prata font-bold text-xl text-[#ff5252] flex items-center gap-2.5"><i className="fas fa-circle-xmark" /> Order Cancelled</h1>
        </header>
        <p className="text-sm text-white/50 mb-4">Order ID: <span className="text-white font-semibold">L-{orderId}</span></p>
        <div className="px-5 py-4 bg-[rgba(255,82,82,0.08)] border border-[rgba(255,82,82,0.4)] rounded-2xl text-[#ff5252] font-medium text-center">
          This order will not be processed.
        </div>
        <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className={whatsappLink}>
          <i className="fab fa-whatsapp text-lg" /> Chat with us on WhatsApp
        </a>
        <div className="mt-5 rounded-2xl overflow-hidden"><img src={foodImage} alt="Order" className="w-full block" /></div>
      </div>
    );
  }

  return (
    <div className={pageBase}>
      <header className="flex items-center justify-between gap-3 flex-wrap mb-6 pt-2">
        <h1 className="font-prata font-bold text-xl">Order L-{orderId}</h1>
        <span className="px-3 py-1 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs font-bold uppercase tracking-wide">
          {orderTypeLabel}
        </span>
      </header>

      {showOriginalThreeStepDesign && (
        <div className={`${infoCard} mb-4`}>
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            // Reaching the final step means the order is fully done, not
            // still "in progress" on it — so treat it as done, not current.
            const isDone = index < originalTrackingConfig.currentStep || (isLast && index === originalTrackingConfig.currentStep);
            const isCurrent = index === originalTrackingConfig.currentStep && !isDone;
            const isActive = isDone || isCurrent;
            return (
              <div key={step.key} className="flex gap-3.5">
                <div className="flex flex-col items-center">
                  <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] transition-colors duration-500 ${
                    isDone ? "bg-brand-orange text-black" : isCurrent ? "bg-brand-orange/15 text-brand-orange ring-2 ring-brand-orange" : "bg-white/[0.06] text-white/30"
                  }`}>
                    {isCurrent && (
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-orange/40" />
                    )}
                    <i className={`fas ${isDone ? "fa-check" : step.icon} relative`} />
                  </span>
                  {!isLast && (
                    <span className={`w-[2px] flex-1 min-h-[28px] my-1 rounded-full transition-colors duration-500 ${isDone ? "bg-brand-orange" : "bg-white/10"}`} />
                  )}
                </div>
                <div className={`pb-5 transition-opacity duration-500 ${isActive ? "opacity-100" : "opacity-45"}`}>
                  <p className={`text-[15px] m-0 ${isCurrent ? "font-bold text-white" : "font-medium text-white/90"}`}>{step.label}</p>
                  <small className="text-xs text-white/40 mt-0.5 block">{step.subtext}</small>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isDelivery && statusSource === "naijameals" && !showOriginalThreeStepDesign && (
        <div className={`${infoCard} mb-4`}>
          <div className="flex items-center gap-3">
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange ring-2 ring-brand-orange">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-orange/40" />
              <i className="fas fa-truck-fast relative text-[13px]" />
            </span>
            <div>
              <p className="text-[15px] font-bold m-0 text-white">{formatApiStatus(status)}</p>
              {!["done", "completed", "delivered"].includes(lowerStatus) && <small className="text-xs text-white/40 mt-0.5 block">Your delivery is being tracked.</small>}
            </div>
          </div>
        </div>
      )}

      {showDriverDetails && (
        <div className={`${infoCard} mt-4`}>
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-brand-orange/10 text-brand-orange text-[17px]"><i className="fas fa-truck" /></div>
            <h3 className="m-0 font-prata font-bold text-white text-[20px]">Delivery Details</h3>
          </div>
          {driver ? (
            <div className="flex flex-col gap-3">
              {[
                { icon: "fas fa-user", label: "Rider Name", value: driver.name || "Not assigned yet" },
                { icon: "fas fa-phone-alt", label: "Phone", value: driver.phone ? <a href={`tel:${driver.phone}`} className="text-inherit no-underline font-semibold hover:underline">{driver.phone}</a> : "Not available" },
                { icon: "fas fa-bicycle", label: "Vehicle", value: driver.transport_type || "N/A" },
                ...(eta?.pickup ? [{ icon: "fas fa-store", label: "Estimated rider pick-up", value: formatEtaOnlyMinutes(eta.pickup) }] : []),
                ...(eta?.dropoff ? [{ icon: "fas fa-clock", label: "Estimated delivery", value: formatEtaOnlyMinutes(eta.dropoff) }] : []),
              ].map(({ icon, label, value }, i) => (
                <div key={i} className={detailRow}>
                  <div className={detailIcon}><i className={icon} /></div>
                  <div className={detailTextWrap}>
                    <span className="text-white/40 text-sm">{label}</span>
                    <strong className="text-white text-[17px] leading-snug break-words">{value}</strong>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2.5 text-white/60 text-[15px]">
              <i className="fas fa-user-clock text-white/40" />
              <p>Driver has not been assigned yet.</p>
            </div>
          )}
          <div className="text-right text-[0.75rem] mt-3 text-white/30 font-medium">
            Powered by <a href="https://naijameals.com" target="_blank" rel="noopener noreferrer" className="text-white/50 font-semibold underline hover:text-white/70">Naijameals.com</a>
          </div>
        </div>
      )}

      <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className={whatsappLink}>
        <i className="fab fa-whatsapp text-lg" /> Chat with us on WhatsApp
      </a>

      <div className="mt-5 rounded-2xl overflow-hidden">
        <img src={foodImage} alt="Order" className="w-full block" />
      </div>
    </div>
  );
}
